import { Close, Edit, Save, ArrowBack, Print } from '@mui/icons-material';
import { AppBar, Box, Button, Tab, Tabs, Grid, IconButton, Divider } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { deepEqual } from 'fast-equals';
import { useSnackbar } from 'notistack';
import { FC, useEffect, useState, useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { EditableCell, Loader, PageTitle } from '../../../../components';
import {
  getSpecOptions,
  getBuildOptionsCategories,
  updateBuildOptions,
  completeContractDocuments
} from '../../../../fetch';
import { formatMoney } from '../../../../helpers';
import {
  ICategory,
  ISpec,
  ISpecCategory,
  ISpecOption,
  ISpecOptionsDetail,
  ISelectedSpecOption,
  ReactSetState,
  ICustomColorScheme
} from '../../../../models';
import { SelectionDetailInfo } from './SelectionDetailsInfo';
import { UserContext } from '../../../../context/user';
import ReactToPrint, { PrintContextConsumer } from 'react-to-print';
import { ContractOptions } from './contract-options';
import { ManageTenDayOptions } from './manage-ten-day-options';
import * as Yup from 'yup';
import { Formik } from 'formik';

const validationSchema = Yup.object().shape({
  financingType: Yup.string().required('Required'),
  lenderId: Yup.string().when('financingType', {
    is: (val: string) => val !== 'Cash',
    then: Yup.string().required('Required')
  })
});

interface ISelectionDetail {
  open: boolean;
  onClose: () => void;
  spec: ISpec;
  selectedOptions: ISelectedSpecOption[];
  category: ICategory;
  fetchCategories: () => Promise<void>;
  setHouseSaleContingencyLocation: ReactSetState<string>;
  houseSaleContingencyLocation: string;
  setHasHouseSaleContingency: ReactSetState<boolean>;
  hasHouseSaleContingency: boolean;
  saveSpec: () => void;
  isSavingSpec: boolean;
  path?: string;
  shouldShowFinishSpecButton?: boolean;
}

enum ManageOptionsTabs {
  Info,
  ContractOptions,
  TenDayOptions
}
export const SelectionDetails: FC<ISelectionDetail> = ({
  open,
  onClose,
  category,
  spec,
  selectedOptions,
  fetchCategories,
  setHouseSaleContingencyLocation,
  houseSaleContingencyLocation,
  setHasHouseSaleContingency,
  hasHouseSaleContingency,
  path,
  saveSpec,
  isSavingSpec,
  shouldShowFinishSpecButton = true
}) => {
  const classes = useStyles();
  const { user } = useContext(UserContext);
  const location = useLocation();
  const fullPath = location.pathname;
  const { enqueueSnackbar } = useSnackbar();
  const [isSaving, setSaving] = useState(false);
  const [selectedTab, setSelectedTab] = useState(ManageOptionsTabs.Info);
  const printRef = useRef();

  const [specOptions, setSpecOptions] = useState<ISpecOptionsDetail | null>(null);
  const [isFetchingOptions, setIsFetchingOptions] = useState<boolean>(false);
  const [contractCategoryOptions, setContractCategoryOptions] = useState<ISpecOption[]>([]);
  const [tenDayCategoryOptions, setTenDayCategoryOptions] = useState<ISpecOption[]>([]);
  const [editableContractCategoryOptions, setEditableContractCategoryOptions] = useState<
    ISpecOption[]
  >([]);
  const [editableTenDayCategoryOptions, setEditableTenDayCategoryOptions] = useState<ISpecOption[]>(
    []
  );
  const hasUnsavedChanges =
    !deepEqual(contractCategoryOptions, editableContractCategoryOptions) ||
    !deepEqual(tenDayCategoryOptions, editableTenDayCategoryOptions);

  const [overrideExistingSelection, setOverrideExistingSelection] = useState<boolean>(false);

  const getBackText = () => {
    if (fullPath.includes('specs')) {
      return 'Back to spec options';
    } else {
      return 'Back to build options';
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      const result = window.confirm('You have unsaved changes, are you sure you want to exit?');
      if (result) {
        setSelectedTab(ManageOptionsTabs.Info);
        onClose();
      } else {
        return;
      }
    } else {
      setSelectedTab(ManageOptionsTabs.Info);
      onClose();
    }
  };

  const fetchOptionDetails = async () => {
    setIsFetchingOptions(true);
    try {
      const res = await getSpecOptions(`${spec.buildId}`);
      setSpecOptions(res);
      setContractCategoryOptions(res.contractOptions);
      setEditableContractCategoryOptions(res.contractOptions);
      setTenDayCategoryOptions(res.tenDayOptions);
      setEditableTenDayCategoryOptions(res.tenDayOptions);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error saving lender info, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsFetchingOptions(false);
    }
  };

  const saveFinancingInfo = async () => {
    try {
      const res = await getSpecOptions(`${spec.buildId}`);
      setSpecOptions(res);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error saving financing info, please try again.`, {
        variant: 'error'
      });
    }
  };

  const [isSavingOptions, setIsSavingOptions] = useState(false);
  const updateOptionDetails = async () => {
    setIsSavingOptions(true);
    try {
      const res = await updateBuildOptions(`${spec.buildId}`, {
        contractOptions: editableContractCategoryOptions.map(option => {
          return {
            //Adding an optionId so I can remove and edit before PUTing to BE
            // optionId for new items shouldn't be sent and will be removed before PUTing
            optionId: option?.optionId?.includes('new-') ? null : option?.optionId,
            category: option.otherClass ? option.otherClass : option.category,
            subCategory: option.otherSubCategory ? option.otherSubCategory : option.subCategory,
            description: option.name,
            quantity: option.quantity,
            totalPrice: option.totalPrice,
            price: option.price,
            canEdit: true
          };
        }),
        tenDayOptions: editableTenDayCategoryOptions.map(option => {
          return {
            //Adding an optionId so I can remove and edit before PUTing to BE
            // optionId for new items shouldn't be sent and will be removed before PUTing
            optionId: option?.optionId?.includes('new-') ? null : option?.optionId,
            category: option.otherClass ? option.otherClass : option.category,
            subCategory: option.otherSubCategory ? option.otherSubCategory : option.subCategory,
            description:
              option.subCategory === 'Exterior Color Scheme'
                ? `${option.customColorScheme?.brickMortarColor}, ${option.customColorScheme?.roofColor}, ${option.customColorScheme?.trimColor}, ${option.customColorScheme?.shutterPaintColor}, ${option.customColorScheme?.windowColor}, ${option.customColorScheme?.garageDoorColor}`
                : option.name,
            quantity: option.quantity,
            totalPrice: option.totalPrice,
            price: option.price,
            canEdit: true,
            customColorScheme: {
              brickMortarColor: option.customColorScheme?.brickMortarColor,
              roofColor: option.customColorScheme?.roofColor,
              trimColor: option.customColorScheme?.trimColor,
              windowColor: option.customColorScheme?.windowColor,
              shutterPaintColor: option.customColorScheme?.shutterPaintColor,
              garageDoorColor: option.customColorScheme?.garageDoorColor
            }
          };
        })
      });
      if (res && res.Detail) {
        return enqueueSnackbar(`Error, ${res.Detail}`, {
          variant: 'error'
        });
      } else if (
        res &&
        res.Errors &&
        Object.values(res.Errors)[0] &&
        Object.values(Object.values(res.Errors)[0])[0]
      ) {
        return enqueueSnackbar(`Error, ${Object.values(Object.values(res.Errors)[0])[0]}`, {
          variant: 'error'
        });
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsSavingOptions(false);
    }
  };

  const [specOptionsCategories, setSpecOptionsCategories] = useState<ISpecCategory[]>([]);
  const [isFetchingOptionsCategories, setIsFetchingOptionsCategories] = useState<boolean>(false);
  const fetchOptionCategories = async () => {
    setIsFetchingOptionsCategories(true);
    try {
      const res = await getBuildOptionsCategories(`${spec.buildId}`);
      setSpecOptionsCategories(res);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error saving lender info, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsFetchingOptionsCategories(false);
    }
  };

  useEffect(() => {
    fetchOptionDetails();
    fetchOptionCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec]);

  const [editingId, setEditingId] = useState<string | null>(null);

  const hasCorrectRole = () => {
    if (user?.userRoles?.includes('BL_Admin') || user?.userRoles?.includes('BL_SalesAdmin')) {
      return true;
    } else {
      return false;
    }
  };

  const updateOptionsData = (
    option: ISpecOption,
    columnId: string,
    value: string | ICustomColorScheme,
    error?: boolean,
    isContract?: boolean
  ) => {
    // Helper function to update description if the option is an exterior color scheme
    const recalcDescription = (opt: ISpecOption) => {
      if (opt.subCategory === 'Exterior Color Scheme' && opt.customColorScheme) {
        const {
          brickMortarColor = '',
          roofColor = '',
          trimColor = '',
          shutterPaintColor = '',
          windowColor = '',
          garageDoorColor = ''
        } = opt.customColorScheme;
        return `${brickMortarColor}, ${roofColor}, ${trimColor}, ${shutterPaintColor}, ${windowColor}, ${garageDoorColor}`;
      }
      return opt.name;
    };

    const updater = (row: ISpecOption) => {
      if (row.optionId === option.optionId) {
        // Create a new updated option
        const updatedOption: ISpecOption = {
          ...option,
          [columnId]: value
        };

        // If updating a customColorScheme field, ensure the customColorScheme object is updated
        // After updating, recalc the description
        if (
          updatedOption.subCategory === 'Exterior Color Scheme' ||
          updatedOption.category === 'Color Scheme Options'
        ) {
          updatedOption.name = recalcDescription(updatedOption);
          updatedOption.totalPrice = updatedOption.price * updatedOption.quantity;
        }
        return updatedOption;
      }
      return row;
    };

    if (isContract) {
      setEditableContractCategoryOptions(old => old.map(updater));
    } else {
      setEditableTenDayCategoryOptions(old => old.map(updater));
    }
  };

  const getColumns = (isContract?: boolean) => {
    return [
      {
        Header: 'Class',
        accessor: 'category',
        isServerSorted: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISpecOption } };
        }) => {
          return <>{original.otherClass ? original.otherClass : original.category}</>;
        }
      },
      {
        Header: 'Subcategory',
        accessor: 'subcategory',
        isServerSorted: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISpecOption } };
        }) => {
          const subCategory = original.otherSubCategory
            ? original.otherSubCategory
            : original.subCategory;
          return (
            <>
              {!original?.subCategoryNote && <>{subCategory}</>}
              {original?.subCategoryNote && (
                <>
                  <strong>{subCategory}</strong>
                  <br />
                  <span>{original?.subCategoryNote}</span>
                </>
              )}
            </>
          );
        }
      },
      {
        Header: 'Name',
        accessor: 'name',
        isServerSorted: false,
        overrideWidth: 300,
        Cell: props => {
          const original = props?.cell?.row?.original as ISpecOption;
          const isEditing = editingId === original.optionId;
          // If the option is for Exterior Color Scheme and is editing,
          // show six EditableCells. Otherwise, show original.name.
          if (original.subCategory === 'Exterior Color Scheme' && isEditing) {
            return (
              <Box display="flex" flexDirection="column" gap={1}>
                <EditableCell
                  value={original.customColorScheme?.brickMortarColor || ''}
                  row={props.cell.row}
                  label="Brick Mortar Color"
                  column={{ id: 'brickMortarColor', Header: 'Brick Mortar Color' }}
                  updateMyData={(orig, id, val, rowIndex) =>
                    updateOptionsData(
                      original,
                      'customColorScheme',
                      {
                        ...original.customColorScheme,
                        brickMortarColor: val
                      },
                      false,
                      isContract
                    )
                  }
                />
                <EditableCell
                  value={original.customColorScheme?.roofColor || ''}
                  row={props.cell.row}
                  column={{ id: 'roofColor', Header: 'Roof Color' }}
                  label="Roof Color"
                  updateMyData={(orig, id, val, rowIndex) =>
                    updateOptionsData(
                      original,
                      'customColorScheme',
                      {
                        ...original.customColorScheme,
                        roofColor: val
                      },
                      false,
                      isContract
                    )
                  }
                />
                <EditableCell
                  value={original.customColorScheme?.trimColor || ''}
                  row={props.cell.row}
                  column={{ id: 'trimColor', Header: 'Trim Color' }}
                  label="Trim Color"
                  updateMyData={(orig, id, val, rowIndex) =>
                    updateOptionsData(
                      original,
                      'customColorScheme',
                      {
                        ...original.customColorScheme,
                        trimColor: val
                      },
                      false,
                      isContract
                    )
                  }
                />
                <EditableCell
                  value={original.customColorScheme?.shutterPaintColor || ''}
                  row={props.cell.row}
                  column={{ id: 'shutterPaintColor', Header: 'Shutter Paint Color' }}
                  label="Shutter Paint Color"
                  updateMyData={(orig, id, val, rowIndex) =>
                    updateOptionsData(
                      original,
                      'customColorScheme',
                      {
                        ...original.customColorScheme,
                        shutterPaintColor: val
                      },
                      false,
                      isContract
                    )
                  }
                />
                <EditableCell
                  value={original.customColorScheme?.windowColor || ''}
                  row={props.cell.row}
                  column={{ id: 'windowColor', Header: 'Window Color' }}
                  label="Window Color"
                  updateMyData={(orig, id, val, rowIndex) =>
                    updateOptionsData(
                      original,
                      'customColorScheme',
                      {
                        ...original.customColorScheme,
                        windowColor: val
                      },
                      false,
                      isContract
                    )
                  }
                />
                <EditableCell
                  value={original.customColorScheme?.garageDoorColor || ''}
                  row={props.cell.row}
                  column={{ id: 'garageDoorColor', Header: 'Garage Door Color' }}
                  label="Garage Door Color"
                  updateMyData={(orig, id, val, rowIndex) =>
                    updateOptionsData(
                      original,
                      'customColorScheme',
                      {
                        ...original.customColorScheme,
                        garageDoorColor: val
                      },
                      false,
                      isContract
                    )
                  }
                />
              </Box>
            );
          } else if (original.subCategory !== 'Exterior Color Scheme' && isEditing) {
            return <EditableCell {...props} />;
          } else {
            // When not editing (or if not an Exterior Color Scheme), display as usual.
            return <span>{original.name}</span>;
          }
        }
      },
      {
        Header: 'Price',
        accessor: 'price',
        isServerSorted: false,
        Cell: props =>
          editingId === props?.cell?.row?.original?.optionId ? (
            <EditableCell {...props} isNumberOnly />
          ) : (
            <span>{formatMoney(props?.cell?.row?.original?.totalPrice)}</span>
          )
      },
      {
        Header: 'Quantity',
        accessor: 'quantity',
        isServerSorted: false,
        Cell: props =>
          editingId === props?.cell?.row?.original?.optionId ? (
            <EditableCell {...props} isNumberOnly />
          ) : (
            <span>{props?.cell?.row?.original?.quantity}</span>
          )
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISpecOption } };
        }) => {
          const isEditing = editingId === original?.optionId;
          return (
            <div className={classes.actionButtons}>
              {original.canEdit && (
                <Button
                  id="edit"
                  color="primary"
                  onClick={() => {
                    isEditing ? setEditingId(null) : setEditingId(original.optionId);
                  }}
                  startIcon={<Edit />}
                >
                  {isEditing ? 'Save' : 'Edit'}
                </Button>
              )}
              <Button
                id="remove"
                onClick={() => {
                  const result = window.confirm('Are you sure you want to remove this item?');
                  if (result) {
                    isContract
                      ? setEditableContractCategoryOptions(
                          editableContractCategoryOptions.filter(
                            option => original.optionId !== option.optionId
                          )
                        )
                      : setEditableTenDayCategoryOptions(
                          editableTenDayCategoryOptions.filter(
                            option => original.optionId !== option.optionId
                          )
                        );
                  } else {
                    return;
                  }
                }}
                startIcon={<Close />}
                className={classes.removeButton}
              >
                Remove
              </Button>
            </div>
          );
        }
      }
    ];
  };
  return (
    <Formik
      enableReinitialize={true}
      validateOnMount
      initialValues={{
        financingType: specOptions?.financingType ?? '',
        lenderId: specOptions?.lenderId ?? ''
      }}
      validationSchema={validationSchema}
      onSubmit={async values => {
        const data = {
          ...values,
          lenderId:
            values.lenderId === 'other' || values?.financingType === 'Cash' ? null : values.lenderId
        };
        try {
          setSaving(true);
          await completeContractDocuments(spec.buildId, data);
          spec.isTenDayComplete = true;
          enqueueSnackbar(`Contract Selections Completed!`, {
            variant: 'success'
          });
        } catch (error) {
          enqueueSnackbar(
            error?.Detail ?? `Error completing contract selections, please try again.`,
            {
              variant: 'error'
            }
          );
        } finally {
          setSaving(false);
        }
      }}
    >
      <>
        {isFetchingOptions && <Loader type="inline" position="centered" />}

        {!isFetchingOptions && spec && specOptions && (
          <div className={'print--none'}>
            <Grid container className={classes.titleWrapper}>
              <Grid item>
                <IconButton title={getBackText()} onClick={() => onClose()} size="large">
                  <ArrowBack />
                </IconButton>
              </Grid>
              <Grid item xs={12} className={classes.pageTitleWrapper}>
                <PageTitle
                  showDivider={false}
                  title={'Selection Details'}
                  additionalHeaderContent={
                    <Box display="flex" gap={1}>
                      <ReactToPrint content={() => printRef.current}>
                        <PrintContextConsumer>
                          {({ handlePrint }) => (
                            <Button
                              id="print-page"
                              variant="contained"
                              color="primary"
                              startIcon={<Print />}
                              onClick={() => {
                                handlePrint();
                              }}
                            >
                              Print
                            </Button>
                          )}
                        </PrintContextConsumer>
                      </ReactToPrint>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
            <Divider className={classes.divider} />
            <AppBar position="static" color="default" component="div" className={classes.tabBar}>
              <Tabs
                value={selectedTab}
                indicatorColor="primary"
                textColor="primary"
                onChange={(_, value) => {
                  setSelectedTab(value);
                }}
                className={classes.tabsWrapper}
              >
                <Tab id="info" aria-controls="info-content" label="Info" />
                <Tab
                  id="manage-contract-options"
                  aria-controls="manage-contract-options-content"
                  label="Manage Contract Options"
                />
                <Tab
                  id="manage-ten-day-options"
                  aria-controls="manage-ten-day-options-content"
                  label="Manage Ten Day Options"
                />
              </Tabs>
            </AppBar>

            {selectedTab === ManageOptionsTabs.Info && (
              <SelectionDetailInfo
                spec={spec}
                selectedOptions={selectedOptions}
                category={category}
                specOptions={specOptions}
                setSaving={setSaving}
                isSaving={isSaving}
                setHouseSaleContingencyLocation={setHouseSaleContingencyLocation}
                houseSaleContingencyLocation={houseSaleContingencyLocation}
                setHasHouseSaleContingency={setHasHouseSaleContingency}
                hasHouseSaleContingency={hasHouseSaleContingency}
                handleClose={handleClose}
                path={path}
                shouldShowFinishSpecButton={
                  shouldShowFinishSpecButton && spec.isSpec && !spec?.clientId
                }
                fetchOptionDetails={saveFinancingInfo}
              />
            )}
            {selectedTab === ManageOptionsTabs.ContractOptions && (
              <ContractOptions
                isFetchingOptionsCategories={isFetchingOptionsCategories}
                hasCorrectRole={hasCorrectRole}
                specOptionsCategories={specOptionsCategories}
                editableContractCategoryOptions={editableContractCategoryOptions}
                setEditableContractCategoryOptions={setEditableContractCategoryOptions}
                setOverrideExistingSelection={setOverrideExistingSelection}
                overrideExistingSelection={overrideExistingSelection}
                spec={spec}
                getColumns={getColumns}
                editingId={editingId}
                setEditingId={setEditingId}
                isFetchingOptions={isFetchingOptions}
                updateOptionsData={updateOptionsData}
              />
            )}
            {selectedTab === ManageOptionsTabs.TenDayOptions && (
              <ManageTenDayOptions
                isFetchingOptionsCategories={isFetchingOptionsCategories}
                hasCorrectRole={hasCorrectRole}
                specOptionsCategories={specOptionsCategories}
                editableTenDayCategoryOptions={editableTenDayCategoryOptions}
                setEditableTenDayCategoryOptions={setEditableTenDayCategoryOptions}
                setOverrideExistingSelection={setOverrideExistingSelection}
                overrideExistingSelection={overrideExistingSelection}
                spec={spec}
                getColumns={getColumns}
                editingId={editingId}
                setEditingId={setEditingId}
                isFetchingOptions={isFetchingOptions}
                updateOptionsData={updateOptionsData}
              />
            )}

            {selectedTab !== ManageOptionsTabs.Info && (
              <Box marginTop="1rem" display="flex">
                <Button
                  type="button"
                  startIcon={<Save />}
                  variant="contained"
                  color="primary"
                  disabled={isSaving || isSavingOptions || !hasUnsavedChanges}
                  onClick={async () => {
                    try {
                      setSaving(true);
                      await updateOptionDetails();
                      enqueueSnackbar(`Updated Contract Options!`, {
                        variant: 'success'
                      });
                      setSelectedTab(ManageOptionsTabs.Info);
                      fetchCategories();
                      onClose();
                    } catch (error) {
                      enqueueSnackbar(`Error saving contract options, please try again.`, {
                        variant: 'error'
                      });
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  Save
                </Button>
                <Box marginLeft="8px">
                  <Button
                    type="button"
                    startIcon={<Close />}
                    variant="contained"
                    color="inherit"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </div>
        )}
        <Box display="none" displayPrint="block" padding={'1rem'} ref={printRef}>
          <PageTitle title={'Selection Details'} />
          <SelectionDetailInfo
            key={'selection-details-info'}
            spec={spec}
            selectedOptions={selectedOptions}
            category={category}
            specOptions={specOptions}
            setSaving={setSaving}
            isSaving={isSaving}
            setHouseSaleContingencyLocation={setHouseSaleContingencyLocation}
            houseSaleContingencyLocation={houseSaleContingencyLocation}
            setHasHouseSaleContingency={setHasHouseSaleContingency}
            hasHouseSaleContingency={hasHouseSaleContingency}
            handleClose={handleClose}
            path={path}
            shouldShowFinishSpecButton={
              shouldShowFinishSpecButton && spec.isSpec && !spec?.clientId
            }
          />

          <ContractOptions
            key={'selection-contract-options'}
            isFetchingOptionsCategories={isFetchingOptionsCategories}
            hasCorrectRole={hasCorrectRole}
            specOptionsCategories={specOptionsCategories}
            editableContractCategoryOptions={editableContractCategoryOptions}
            setEditableContractCategoryOptions={setEditableContractCategoryOptions}
            setOverrideExistingSelection={setOverrideExistingSelection}
            overrideExistingSelection={overrideExistingSelection}
            spec={spec}
            getColumns={getColumns}
            editingId={editingId}
            setEditingId={setEditingId}
            isFetchingOptions={isFetchingOptions}
            updateOptionsData={updateOptionsData}
            isPrint
          />

          <ManageTenDayOptions
            key={'selection-ten-day-options'}
            isFetchingOptionsCategories={isFetchingOptionsCategories}
            hasCorrectRole={hasCorrectRole}
            specOptionsCategories={specOptionsCategories}
            editableTenDayCategoryOptions={editableTenDayCategoryOptions}
            setEditableTenDayCategoryOptions={setEditableTenDayCategoryOptions}
            setOverrideExistingSelection={setOverrideExistingSelection}
            overrideExistingSelection={overrideExistingSelection}
            spec={spec}
            getColumns={getColumns}
            editingId={editingId}
            setEditingId={setEditingId}
            isFetchingOptions={isFetchingOptions}
            updateOptionsData={updateOptionsData}
            isPrint
          />
        </Box>
      </>
    </Formik>
  );
};
const useStyles = makeStyles((theme: Theme) => ({
  tabBar: {
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  },
  tabsWrapper: {
    '&& .MuiTabs-flexContainer': {
      overflowX: 'auto'
    }
  },
  actionButtons: {
    display: 'flex',
    textAlign: 'right'
  },
  root: {
    marginTop: theme.spacing(1),
    paddingRight: theme.spacing(0.5)
  },
  override: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1)
  },
  removeButton: {
    color: theme.palette.error.main
  },
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },
  titleWrapper: {
    flexWrap: 'nowrap'
  },
  divider: {
    marginBottom: theme.spacing(1)
  }
}));
